/**
 * Shows a brief or script as written (docs/RICH-TEXT.md): the formatted copy when there
 * is one, cleaned again here before it reaches the page, else the plain text one
 * paragraph per line. Every paragraph sets its own direction, so Arabic reads right to
 * left, English left to right, and a line mixing both keeps its words in order.
 */
import DOMPurify from 'dompurify';
import { cn } from '@/lib/cn';

/** Shared look for the editor and the viewer: per-paragraph bidi, lists, the highlighter. */
export const RICH_CLS = [
  'leading-relaxed [overflow-wrap:anywhere]',
  // a paragraph left on Auto finds its own direction line by line; a forced one keeps it
  // (plaintext would override an explicit rtl or ltr, 2026-10-02)
  '[&_p]:my-1 [&_[dir=auto]]:[unicode-bidi:plaintext] [&_p:not([dir])]:[unicode-bidi:plaintext]',
  '[&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-2 [&_h3]:font-semibold [&_h3]:mt-2',
  '[&_ul]:list-disc [&_ol]:list-decimal [&_ul]:ps-5 [&_ol]:ps-5 [&_li]:my-0.5',
  '[&_mark]:rounded-sm [&_mark]:px-0.5 [&_mark]:!text-[#111827] [&_mark_*]:!text-[#111827]',
  '[&_blockquote]:border-s-2 [&_blockquote]:border-slate-600 [&_blockquote]:ps-3',
].join(' ');

const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** The plain text of a formatted copy, one line per paragraph (as the server derives it). */
export function htmlToPlain(html: string): string {
  if (!html) return '';
  const t = html.replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|h1|h2|h3|li|blockquote)>/gi, '\n').replace(/<[^>]+>/g, '');
  const d = document.createElement('textarea'); d.innerHTML = t;
  return d.value.replace(/\u00a0/g, ' ').split('\n').map((l) => l.replace(/[ \t]+/g, ' ').trim()).join('\n').replace(/^\n+|\n+$/g, '');
}

/** Plain text as paragraphs, one per line, each finding its own direction. */
export function plainToHtml(plain: string): string {
  const lines = (plain || '').split('\n');
  return lines.length && plain ? lines.map((l) => `<p dir="auto">${l ? esc(l) : '<br>'}</p>`).join('') : '';
}

const ALLOWED = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'span', 'mark', 'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'blockquote'],
  ALLOWED_ATTR: ['dir', 'style', 'data-color'],
};

export function safeHtml(html: string): string {
  return DOMPurify.sanitize(html, ALLOWED) as unknown as string;
}

export default function RichView({ html, plain, className, testid }: { html?: string | null; plain?: string | null; className?: string; testid?: string }) {
  const body = html ? safeHtml(html) : plainToHtml(plain ?? '');
  if (!body) return null;
  return <div className={cn(RICH_CLS, className)} data-testid={testid} dangerouslySetInnerHTML={{ __html: body }} />;
}
